// usePresence.ts — heartbeat hook: updates last_seen + online status every 5 min
// Call once in AuthContext or Layout for internal users

import { useEffect } from 'react'
import { supabase } from '../lib/supabase'

export function usePresence(userId: string | undefined, orgId: string | undefined) {
  useEffect(() => {
    if (!userId || !orgId) return

    async function heartbeat() {
      await supabase.from('chat_presence').upsert(
        {
          user_id: userId,
          org_id: orgId,
          is_online: true,
          last_seen: new Date().toISOString(),
          status: 'active',
        },
        { onConflict: 'user_id' }
      )

      // Also update last_seen on users table
      await supabase.from('users').update({ last_seen: new Date().toISOString() }).eq('id', userId)
    }

    heartbeat() // fire immediately on mount

    const interval = setInterval(heartbeat, 5 * 60 * 1000) // every 5 min

    // Mark offline on unload
    function markOffline() {
      navigator.sendBeacon(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/chat_presence?user_id=eq.${userId}`,
        JSON.stringify({ is_online: false, last_seen: new Date().toISOString() })
      )
    }
    window.addEventListener('beforeunload', markOffline)

    return () => {
      clearInterval(interval)
      window.removeEventListener('beforeunload', markOffline)
      // Mark offline on unmount
      supabase.from('chat_presence').upsert(
        {
          user_id: userId,
          org_id: orgId,
          is_online: false,
          last_seen: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      )
    }
  }, [userId, orgId])
}
